import GoogleMapReact from 'google-map-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import cdConfig from '~/assets/mapStyle/cd.json';
import localeeConfig from '~/assets/mapStyle/localee.json';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { ModalType } from '~/components/Modal/interface';
import {
  deliveryAddressStore,
  mapZoom,
} from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import type { DeliveryZoneRef } from '~/components/ModalDeliveryAddress/GMap/interfaces';
import { createPolygonForZone } from '~/components/ModalDeliveryAddress/GMap/utils';
import Panel from '~/components/Panel/Panel';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { userStore } from '~/stores';
import { GOOGLE_API_KEY } from '~/stores/constants';

import styles from './MapStatic.module.scss';

const mapStyle = company.match({
  citydrinks: cdConfig,
  default: localeeConfig,
});

const MapStatic = () => {
  const [defaultCenter] = useState<GeoCoordinates>(() =>
    deliveryAddressStore.isActiveGeolocation &&
    deliveryAddressStore.geolocationCoordinates
      ? deliveryAddressStore.geolocationCoordinates
      : userStore.deliveryAddress?.coordinates ||
        deliveryAddressStore.mapCenter,
  );
  const isHome = userStore.deliveryAddress?.type?.toLowerCase() === 'home';

  const { t } = useTranslation();
  const { openModal } = useModal();
  const { isMobile } = useGlobal();

  const handleDeliveryAddress = () => {
    if (userStore.addressList.length > 0) {
      openModal(ModalType.DeliveryAddressList);
    } else {
      openModal(ModalType.EditAddressLocation);
    }
  };

  const handleApiLoaded = async ({ map }: { map: google.maps.Map }) => {
    try {
      const coverages = await userStore.getDeliveryZones();
      if (!coverages.length) {
        return;
      }

      const polygonsRef: Record<string, DeliveryZoneRef> = {};

      coverages.forEach((el) => {
        new Map(
          el.zones
            .sort((a, b) => a.priority - b.priority)
            .map((zone) => {
              zone.warehouseId = el.warehouse.id;
              return [zone.name, zone];
            }),
        ).forEach((zone) => {
          const drawPolygons = createPolygonForZone(zone);
          drawPolygons.setMap(map);

          const zoneRef = {
            zone,
            polygon: drawPolygons,
          };

          if (zone.warehouseId) {
            polygonsRef[`${zone.name}-${zone.warehouseId}`] = zoneRef;
          }
        });
      });

      deliveryAddressStore.renderedZonesOnMap = polygonsRef;

      if (userStore.isAuthorized) {
        await userStore.fetchAddresses();
      }
    } catch (error) {
      error && console.error(error);
    }
  };

  const center: GeoCoordinates =
    deliveryAddressStore.isActiveGeolocation &&
    deliveryAddressStore.geolocationCoordinates
      ? deliveryAddressStore.geolocationCoordinates
      : userStore.deliveryAddress?.coordinates ||
        deliveryAddressStore.mapCenter;

  const addressInner = userStore.deliveryAddress?.address1 ? (
    <div className={styles.address}>
      <p className={styles.addressType}>{userStore.deliveryAddress?.type}</p>
      <p className={styles.addressValue}>
        {userStore.deliveryAddress?.address1}
      </p>
    </div>
  ) : (
    <p className={styles.address__Empty}>{t('selectDeliveryAddress')}</p>
  );

  return (
    <div className={styles.container}>
      <Icon
        type="pin"
        size={company.match({
          citydrinks: 44,
          v3: 44,
          default: 64,
        })}
        className={styles.pin}
      />
      <GoogleMapReact
        yesIWantToUseGoogleMapApiInternals
        bootstrapURLKeys={{
          key: GOOGLE_API_KEY,
          language: 'en-GB',
          region: 'GB',
        }}
        defaultCenter={defaultCenter}
        defaultZoom={mapZoom}
        zoom={deliveryAddressStore.mapZoom}
        center={center}
        options={{
          clickableIcons: false,
          disableDefaultUI: true,
          gestureHandling: 'greedy',
          styles: mapStyle,
          draggable: false,
        }}
        onGoogleApiLoaded={handleApiLoaded}
        shouldUnregisterMapOnUnmount={true}
        style={{
          pointerEvents: deliveryAddressStore.isChangeDeliveryAreaPopover
            ? 'none'
            : 'auto',
        }}
      />

      <Panel
        classNames={styles.panel}
        contentClassNames={styles.panelContent}
        onClick={handleDeliveryAddress}
        icon={isHome ? 'house-pin' : 'location'}
        isCompact={!isMobile}
        hasBorder={false}
      >
        {addressInner}
      </Panel>
    </div>
  );
};

export default observer(MapStatic);
